// Internal linking map for service detail pages.
// glossaryLinks → slugs from src/lib/glossary.ts
// blogLinks     → slugs from src/lib/blog.ts
// Empty arrays / omitted keys render no column. Fully empty → block hidden.
// Verified against existing slugs — do not add slugs that don't exist.

export type ServiceLinkSet = {
  glossaryLinks?: string[];
  blogLinks?: string[];
};

export const SERVICE_LINKS: Record<string, ServiceLinkSet> = {
  // ─── Einkommensversicherung ───
  berufsunfaehigkeitsversicherung: {
    glossaryLinks: [
      "berufsunfaehigkeitsversicherung",
      "berufsunfaehigkeitsrente",
      "erwerbsminderungsrente",
      "arbeitgeberzuschuss",
    ],
    blogLinks: [
      "berufsunfaehigkeitsversicherung-sinnvoll",
      "arbeitskraft-absichern-versicherungen",
      "rentenluecke-berechnen-schliessen",
    ],
  },
  grundfaehigkeitenversicherung: {
    glossaryLinks: [
      "grundfaehigkeitsversicherung",
      "berufsunfaehigkeitsversicherung",
      "erwerbsminderungsrente",
    ],
    blogLinks: ["arbeitskraft-absichern-versicherungen"],
  },
  risikolebensversicherung: {
    glossaryLinks: ["lebensversicherung", "hinterbliebenenversorgung", "witwenrente"],
    blogLinks: [
      "risikolebensversicherung-wer-braucht-sie",
      "vermoegen-vererben-schenkung-erbschaft",
    ],
  },
  erwerbsminderungsversicherung: {
    glossaryLinks: [
      "erwerbsminderungsrente",
      "berufsunfaehigkeitsversicherung",
      "gesetzliche-rentenversicherung",
    ],
    blogLinks: [
      "arbeitskraft-absichern-versicherungen",
      "rentenluecke-berechnen-schliessen",
    ],
  },
  dienstunfaehigkeitsversicherung: {
    glossaryLinks: [
      "berufsunfaehigkeitsversicherung",
      "berufsunfaehigkeitsrente",
      "beitragsbemessungsgrenze",
    ],
    blogLinks: ["arbeitskraft-absichern-versicherungen"],
  },

  // ─── Sachversicherungen ───
  privathaftpflicht: {
    glossaryLinks: ["haftpflichtversicherung", "selbstbehalt", "versicherungslucke"],
    blogLinks: ["haftpflichtversicherung-wichtigkeit"],
  },
  hausratversicherung: {
    glossaryLinks: ["sachwerte", "selbstbehalt", "versicherungslucke"],
    blogLinks: ["hausratversicherung-leistungen"],
  },
  wohngebaeudeversicherung: {
    glossaryLinks: ["sachwerte", "hypothek", "immobilienkredit"],
  },
  "kfz-versicherung": {
    glossaryLinks: ["kaskoversicherung", "selbstbehalt", "schufa"],
  },
  unfallversicherung: {
    glossaryLinks: [
      "unfallversicherung",
      "gesetzliche-unfallversicherung",
      "pflegegrad",
      "selbstbehalt",
    ],
    blogLinks: [
      "arbeitskraft-absichern-versicherungen",
      "pflegeversicherung-luecke-absicherung",
    ],
  },
  tierhalterhaftpflicht: {
    glossaryLinks: ["haftpflichtversicherung", "selbstbehalt"],
    blogLinks: ["haftpflichtversicherung-wichtigkeit"],
  },
  rechtsschutzversicherung: {
    glossaryLinks: ["selbstbehalt", "versicherungslucke", "wechselkosten"],
  },
  fahrradversicherung: {
    glossaryLinks: ["sachwerte", "selbstbehalt"],
    blogLinks: ["hausratversicherung-leistungen"],
  },

  // ─── Gesundheitsvorsorge ───
  "private-krankenvollversicherung": {
    glossaryLinks: [
      "private-krankenversicherung",
      "gkv",
      "versicherungspflichtgrenze",
      "beitragsbemessungsgrenze",
    ],
    blogLinks: ["private-krankenversicherung-wechsel", "beitragsbemessungsgrenze-2026"],
  },
  zahnzusatzversicherung: {
    glossaryLinks: ["gkv", "private-krankenversicherung", "selbstbehalt"],
    blogLinks: ["private-krankenversicherung-wechsel"],
  },
  krankentagegeld: {
    glossaryLinks: ["krankengeld", "lohnfortzahlung", "aussteuerung", "beitragsbemessungsgrenze"],
    blogLinks: ["krankentagegeld-luecke-absicherung", "beitragsbemessungsgrenze-2026"],
  },
  pflegezusatzversicherung: {
    glossaryLinks: ["pflegeversicherung", "pflegegrad", "pflegeluecke", "langzeitpflege"],
    blogLinks: [
      "pflegeversicherung-luecke-absicherung",
      "pflegemindestlohn-2026-kosten-absicherung",
    ],
  },
  auslandsreisekrankenversicherung: {
    glossaryLinks: ["private-krankenversicherung", "selbstbehalt"],
  },

  // ─── Altersvorsorge & Vermögensaufbau ───
  "private-rentenversicherung": {
    glossaryLinks: [
      "privatrente",
      "fondsgebundene-rentenversicherung",
      "nachgelagerte-besteuerung",
      "rentenluecke",
    ],
    blogLinks: [
      "fondsgebundene-rentenversicherung",
      "rentenluecke-berechnen-schliessen",
      "altersvorsorge-frauen-rentenluecke",
    ],
  },
  "ruerup-rente": {
    glossaryLinks: ["basisrente", "ruerup-rente", "sonderausgaben", "vorsorgeaufwendungen"],
    blogLinks: [
      "rurup-rente-steuern-sparen",
      "steuern-sparen-altersvorsorge",
      "staatliche-foerderungen-altersvorsorge-2026",
    ],
  },
  "betriebliche-altersvorsorge": {
    glossaryLinks: [
      "betriebliche-altersvorsorge",
      "entgeltumwandlung",
      "direktversicherung",
      "arbeitgeberzuschuss",
    ],
    blogLinks: [
      "betriebliche-altersvorsorge-steuerfrei",
      "steuern-sparen-altersvorsorge",
      "beitragsbemessungsgrenze-2026",
    ],
  },
  "etf-sparplan": {
    glossaryLinks: ["etf", "sparplan", "msci-world", "cost-average-effekt"],
    blogLinks: [
      "etf-portfolio-aufbauen-anleitung",
      "sparplan-vs-einmalanlage",
      "vermoegensaufbau-etf",
    ],
  },
  "immobilien-kapitalanlage": {
    glossaryLinks: ["immobilie-kapitalanlage", "mietrendite", "afa", "vermietung-versteuerung"],
    blogLinks: ["immobilien-kapitalanlage-grundlagen"],
  },
  "riester-rente": {
    glossaryLinks: ["riester-rente", "altersvorsorgedepot", "zulagenantrag", "kinderzulage"],
    blogLinks: [
      "altersvorsorgedepot-2027-riester-nachfolger",
      "staatliche-foerderungen-altersvorsorge-2026",
    ],
  },
  "altersvorsorgedepot-2027": {
    glossaryLinks: ["altersvorsorgedepot", "riester-rente", "kinderzulage", "foerderungen"],
    blogLinks: [
      "altersvorsorgedepot-2027-riester-nachfolger",
    ],
  },

  // ─── Gewerbeversicherung ───
  // Services 26–33, 35, 36 intentionally omitted: only generic haftpflicht/selbstbehalt
  // pairs would apply, which is filler rather than real thematic connection.
  // Leave the block hidden until service-specific glossary entries exist
  // (elementarschaden, kfz-typklasse, cyberrisiko, d-und-o-versicherung, deckungssumme, …).
  "do-versicherung": {
    glossaryLinks: ["insolvenz"],
    blogLinks: ["arbeitgeber-insolvenz-arbeitnehmer-rechte-absicherung"],
  },
  betriebsunterbrechungsversicherung: {
    glossaryLinks: ["insolvenz", "liquiditaet", "cashflow"],
  },

  // ─── Finanzierung ───
  immobilienkredit: {
    glossaryLinks: ["immobilienkredit", "hypothek", "tilgung", "sondertilgung"],
  },
  konsumkredit: {
    glossaryLinks: ["dispositionskredit", "bonitaet", "schufa", "zins"],
  },
};
